/**
 * App or Root Module
 * it identify how the Vixxo App is being constructed
 */
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  ErrorHandler,
  Injector,
  NgModule,
  NgModuleFactoryLoader,
  SystemJsNgModuleLoader
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MsalGuard, MsalModule } from '@azure/msal-angular';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AssetModule } from '../modules/asset/asset.module';
import { AssetTypeModule } from '../modules/assetType/asset-type.module';
import { BulkAddModule } from '../modules/bulkAdd/bulk-add.module';
import { CustomerUserModule } from '../modules/customerUser/customer-user.module';
import { IssueModule } from '../modules/issue/issue.module';
import { MakeModule } from '../modules/make/make.module';
import { PartCategoryModule } from '../modules/partCategory/part-category.module';
import { PartsModule } from '../modules/parts/parts.module';
import { PartTypeModule } from '../modules/partType/part-type.module';
import { ProductModule } from '../modules/product/product.module';
import { QuestionModule } from '../modules/question/question.module';
import { SecurityModule } from '../modules/security/security.module';
import { SPUserModule } from '../modules/spUser/sp-user.module';
import { SurveyManagementModule } from '../modules/surveyManagement/survey-management.module';
import { UserModule } from '../modules/user/user.module';
import { GlobalErrorHandler } from '../shared/errors/global-error-handler';
import { AuthGuard } from '../shared/guards/auth.guards';
import { PendingChangesGuard } from '../shared/guards/pending-changes.guard';
import { HttpRequestInterceptor } from '../shared/providers/http-request-interceptor.provider';
import { IndexLoggingService } from '../shared/services/index-logging.service';
import { SharedModule } from '../shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

const LocalEnv = require('../../../server-console/shared/localEnv.js');
const CLIENT_ID = process.env.CLIENT_ID || LocalEnv.CLIENT_ID;
const AUTHORITY = process.env.AUTHORITY || LocalEnv.AUTHORITY;
const REDIRECTURL = process.env.AUTH0_REDIRECT || LocalEnv.AUTH0_REDIRECT;
const KNOWNAUTHORITIES =
  process.env.KNOWNAUTHORITIES || LocalEnv.KNOWNAUTHORITIES;
const CACHELOCATION = process.env.CACHELOCATION || LocalEnv.CACHELOCATION;
const OPENIDSCOPES = process.env.OPENIDSCOPES || LocalEnv.OPENIDSCOPES;
const OFFLINE_ACCESS = process.env.OFFLINE_ACCESS || LocalEnv.OFFLINE_ACCESS;

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AssetModule,
    MakeModule,
    ProductModule,
    SecurityModule,
    BulkAddModule,
    IssueModule,
    AssetTypeModule,
    PartTypeModule,
    UserModule,
    QuestionModule,
    PartsModule,
    PartCategoryModule,
    SPUserModule,
    CustomerUserModule,
    SurveyManagementModule,
    AppRoutingModule,
    ToastrModule.forRoot(),
    SharedModule.forRoot(),
    // Initiate the MSAL library with the MSAL configuration object
    MsalModule.forRoot(
      {
        auth: {
          clientId: CLIENT_ID,
          authority: AUTHORITY,
          redirectUri: REDIRECTURL,
          knownAuthorities: [KNOWNAUTHORITIES]
        },
        cache: {
          cacheLocation: CACHELOCATION,
          storeAuthStateInCookie: true // set to true for IE 11
        }
      },
      {
        consentScopes: [OPENIDSCOPES, OFFLINE_ACCESS],
        unprotectedResources: ['https://www.microsoft.com/en-us/'],
        protectedResourceMap: [],
        extraQueryParameters: {}
      }
    )
  ],
  declarations: [AppComponent],
  providers: [
    AuthGuard,
    IndexLoggingService,
    MsalGuard, // use on the protected routes ,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
      deps: [Injector, IndexLoggingService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      useFactory: (toastrService: ToastrService, progressService: NgProgress) =>
        new HttpRequestInterceptor(toastrService, progressService),
      deps: [ToastrService, NgProgress],
      multi: true
    },
    PendingChangesGuard,
    { provide: NgModuleFactoryLoader, useClass: SystemJsNgModuleLoader }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
